"use client";

import { m as motion } from "framer-motion";
import Image from "next/image";
import { Section } from "@/components/ui/section";
import { ArrowRight, FileText, Sparkles, Github, Linkedin, Mail, Code2 } from "lucide-react";
import { useEffect, useState } from "react";
import { HERO_DATA } from "@/lib/data";
import Link from "next/link";
import { useTheme } from "next-themes";
import { cn } from "@/lib/utils";
import React from "react";
import { Magnetic } from "@/components/ui/magnetic";

export default function Hero({ data }: { data?: any }) {
  const heroData = data || HERO_DATA;
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const avatarSrc = "/sachinsharma.webp";

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
  };

  return (
    <Section
      id="home"
      className="min-h-[100dvh] flex flex-col justify-center items-center relative overflow-hidden py-20 md:py-0"
    >
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] z-0 mix-blend-overlay"
        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }}
      />

      <div className="container mx-auto px-4 z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 xl:grid-cols-12 gap-12 items-center"
        >
          {/* Left: Content Column */}
          <div className="xl:col-span-7 flex flex-col items-center xl:items-start text-center xl:text-left space-y-6 md:space-y-8 order-2 xl:order-1 relative z-20">
            
            {/* Mobile Avatar (Circular & Small as requested) - Replaces the 3D card on mobile/tablet */}
            <motion.div 
               variants={itemVariants} 
               className="xl:hidden mb-4 relative w-24 h-24 rounded-full border-2 border-primary/20 shadow-2xl overflow-hidden ring-4 ring-background"
            >
               <Image
                 src={avatarSrc}
                 alt="Sachin Sharma"
                 fill
                 priority
                 className="object-cover contrast-[1.05] saturate-[1.2] brightness-[1.1] dark:brightness-[0.85] hover:saturate-[1.4] transition-all duration-700"
               />
            </motion.div>

            {/* Status Badge */}
            <motion.div variants={itemVariants} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 backdrop-blur-sm hover:bg-primary/20 transition-colors cursor-default">
              <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary"></span>
              </span>
              <span className="text-[10px] md:text-xs font-bold text-primary uppercase tracking-widest truncate">
                {heroData?.availabilityStatus || "Available for new projects"}
              </span>
            </motion.div>

            {/* Main Headings */}
            <div className="space-y-4">
              <motion.h2 variants={itemVariants} className="text-muted-foreground text-lg md:text-xl font-medium flex items-center justify-center xl:justify-start gap-2">
                {heroData?.greeting || "Hi, I'm"}
                <motion.span
                  animate={{ rotate: [0, 14, -8, 14, -4, 10, 0] }}
                  transition={{ repeat: Infinity, repeatDelay: 3, duration: 1.5 }}
                  className="origin-bottom-right inline-block"
                >
                  👋
                </motion.span>
              </motion.h2>

              <motion.h1 variants={itemVariants} className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black tracking-tight leading-[1.1] bg-gradient-to-r from-foreground via-foreground to-muted-foreground bg-clip-text text-transparent">
                {heroData?.name || "Sachin Sharma"}
              </motion.h1>

              <motion.div variants={itemVariants} className="flex flex-wrap items-center justify-center xl:justify-start gap-x-2 sm:gap-x-3 gap-y-1">
                {heroData?.roles?.map((role: string, idx: number) => (
                  <React.Fragment key={role}>
                    <span className="text-lg sm:text-2xl md:text-3xl font-serif italic text-foreground/80">{role}</span>
                    {idx < (heroData?.roles?.length || 0) - 1 && (
                      <span className="text-primary/30 text-lg sm:text-xl font-light select-none font-sans">/</span>
                    )}
                  </React.Fragment>
                )) || (
                  <span className="font-serif italic text-xl md:text-3xl text-foreground/80">Software Engineer</span>
                )}
              </motion.div>
            </div>

            <motion.p variants={itemVariants} className="text-sm sm:text-lg text-muted-foreground leading-relaxed max-w-xl mx-auto xl:mx-0 opacity-80 font-medium">
              {heroData?.description || "Building digital experiences that bridge functionality and aesthetics."}
            </motion.p>

            {/* Stats Bar */}
            <motion.div variants={itemVariants} className="grid grid-cols-2 gap-8 py-6 border-y border-border/40 w-full max-w-xs xl:max-w-md mx-auto xl:mx-0">
              {(heroData?.stats || []).map((stat: any) => (
                <Link
                  key={stat.label}
                  href={stat.href}
                  className="text-left group cursor-pointer"
                >
                  <div className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tighter group-hover:text-primary transition-colors duration-300">
                    {stat.value}
                  </div>
                  <div className="text-[10px] md:text-xs text-muted-foreground font-bold uppercase tracking-[0.2em] mt-1 opacity-60 group-hover:opacity-100 group-hover:text-primary transition-all">
                    {stat.label}
                  </div>
                </Link>
              ))}
            </motion.div>

            {/* Buttons & Socials */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row flex-wrap items-center gap-4 pt-2 w-full justify-center xl:justify-start">
              <div className="flex items-center gap-3">
                <Magnetic>
                  <Link
                    href={heroData?.ctaLink || "#projects"}
                    className="group relative inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-bold transition-all hover:translate-y-[-2px] hover:shadow-xl hover:shadow-primary/20"
                  >
                    {heroData?.ctaText || "Explore Work"} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Magnetic>

                <Magnetic>
                  <Link
                    href={heroData?.resumeUrl || "/resume"}
                    className="group inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-border bg-background/50 backdrop-blur-md text-sm font-bold hover:bg-secondary transition-all hover:translate-y-[-2px]"
                  >
                    <FileText className="w-4 h-4 text-primary" />
                    Resume
                  </Link>
                </Magnetic>
              </div>

              {/* Social Icons */}
              <div className="flex items-center gap-2 sm:ml-4">
                <div className="h-8 w-px bg-border/60 mx-2 hidden sm:block" />
                <div className="flex gap-1.5">
                  {(heroData?.socialLinks || []).map((social: any) => (
                    <Magnetic key={social.name}>
                      <Link
                        href={social.href}
                        target={social.href.startsWith('mailto') ? undefined : "_blank"}
                        className={cn(
                          "flex items-center justify-center w-10 h-10 rounded-lg text-muted-foreground hover:bg-secondary/80 transition-all",
                          social.color
                        )}
                        aria-label={`Visit my ${social.name}`}
                      >
                        {social.icon === "Github" && <Github className="w-5 h-5" />}
                        {social.icon === "Linkedin" && <Linkedin className="w-5 h-5" />}
                        {social.icon === "Mail" && <Mail className="w-5 h-5" />}
                      </Link>
                    </Magnetic>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right: Visual Column - Only visible on XL screens as per request to keep mobile simple */}
          <div className="hidden xl:flex xl:col-span-5 relative order-1 xl:order-2 w-full justify-center xl:justify-end">
             <motion.div
               variants={itemVariants}
               className="relative w-full aspect-square max-w-[420px]"
             >
               <motion.div
                 transition={{ type: "spring", stiffness: 300, damping: 20 }}
                 className="relative z-10 w-full h-full p-4 rounded-[40px] border border-white/10 bg-white/5 backdrop-blur-2xl shadow-2xl overflow-hidden"
               >
                 <div className="relative w-full h-full rounded-[28px] overflow-hidden group">
                   <Image
                     src={avatarSrc}
                     alt="Sachin Sharma Portfolio"
                     fill
                     priority
                     sizes="500px"
                     className="object-cover w-full h-full transform transition-all duration-700 group-hover:scale-105 contrast-[1.05] saturate-[1.2] brightness-[1.1] dark:brightness-[0.85] group-hover:saturate-[1.4]"
                   />
                   <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60" />
                   
                   {/* Floating Card */}
                   <motion.div
                     initial={{ opacity: 0, y: 20 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ delay: 1 }}
                     className="absolute bottom-6 left-6 right-6 p-4 rounded-2xl bg-black/40 backdrop-blur-md border border-white/10"
                   >
                     <p className="text-xs font-semibold text-white/90 leading-tight">
                       "Crafting digital experiences that bridge functionality and aesthetics."
                     </p>
                   </motion.div>
                 </div>
               </motion.div>

               {/* Floating Icons */}
               <motion.div
                 className="absolute -top-6 -right-6 z-20"
                 animate={{ y: [0, -10, 0] }}
                 transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
               >
                 <div className="p-4 rounded-2xl bg-background/80 border border-border shadow-xl backdrop-blur-sm">
                   <Sparkles className="w-6 h-6 text-primary" />
                 </div>
               </motion.div>

               <motion.div
                 className="absolute -bottom-6 -left-6 z-20"
                 animate={{ y: [0, 10, 0] }}
                 transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
               >
                 <div className="p-4 rounded-2xl bg-background/80 border border-border shadow-xl backdrop-blur-sm">
                   <Code2 className="w-6 h-6 text-primary" />
                 </div>
               </motion.div>
             </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        className="absolute bottom-8 left-0 right-0 flex flex-col items-center justify-center pointer-events-none z-20"
      >
        <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mr-[-0.2em] opacity-60 mb-2">Scroll</span>
        <div className="w-[1.5px] h-10 bg-gradient-to-b from-primary/50 to-transparent" />
      </motion.div>
    </Section>
  );
}
